import type { TenantEntry } from '@/api/types';

export const TENANT_LIST_CACHE_KEY = 'scope-switch-tenants:current';
export const TENANT_LIST_SESSION_KEY = 'scope-switch-session:current';

const readSession = (): string | null => {
  try {
    return localStorage.getItem(TENANT_LIST_SESSION_KEY);
  } catch {
    return null;
  }
};

let tenantList: TenantEntry[] | null = null;
let pending: Promise<TenantEntry[]> | null = null;
let sessionVersion = 0;
let cachedSession = readSession();

const isTenantList = (value: unknown): value is TenantEntry[] =>
  Array.isArray(value) &&
  value.every(
    (tenant) =>
      tenant &&
      ['string', 'number'].includes(typeof tenant.tenantId) &&
      typeof tenant.tenantName === 'string' &&
      Array.isArray(tenant.brandUserRelationList) &&
      tenant.brandUserRelationList.every(
        (relation) =>
          relation &&
          ['data_brand', 'data_shop_group', 'data_shop'].includes(relation.type) &&
          ['string', 'number'].includes(typeof relation.typeId) &&
          typeof relation.relationName === 'string',
      ),
  );

export const getTenantListCache = (): TenantEntry[] | null => {
  const currentSession = readSession();
  if (cachedSession !== currentSession) {
    // 标签页恢复时也核对登录会话，不能复用休眠前另一个账号的列表。
    sessionVersion++;
    tenantList = null;
    pending = null;
    cachedSession = currentSession;
  }
  if (tenantList !== null) return tenantList;
  try {
    const stored = JSON.parse(sessionStorage.getItem(TENANT_LIST_CACHE_KEY) || 'null');
    if (stored?.session === currentSession && isTenantList(stored.tenants)) tenantList = stored.tenants;
  } catch {
    // 缓存损坏或存储被禁用时，回退到接口获取。
  }
  return tenantList;
};

export const clearTenantListCache = () => {
  sessionVersion++;
  tenantList = null;
  pending = null;
  cachedSession = readSession();
  try {
    sessionStorage.removeItem(TENANT_LIST_CACHE_KEY);
  } catch {
    // 禁用浏览器存储时仍清理当前页面内的会话缓存。
  }
};

/** 登录和切换面板共用；空列表同样缓存，并合并同一会话中的并发请求。 */
export const loadTenantListCache = async (fetchList: () => Promise<TenantEntry[]>): Promise<TenantEntry[]> => {
  const cached = getTenantListCache();
  if (cached !== null) return cached;
  if (pending) return pending;

  const version = sessionVersion;
  const session = cachedSession;
  const request = (async () => {
    const data = await fetchList();
    // 退出或重新登录后，旧账号尚未完成的请求不能回写或返回旧列表。
    if (version !== sessionVersion || session !== readSession()) throw new Error('Tenant list request belongs to an earlier session');
    const normalized = data.map((tenant) => ({ ...tenant, brandUserRelationList: tenant.brandUserRelationList || [] }));
    tenantList = normalized;
    try {
      sessionStorage.setItem(TENANT_LIST_CACHE_KEY, JSON.stringify({ session, tenants: normalized }));
    } catch {
      // 存储不可用时，当前页面仍复用内存缓存。
    }
    return normalized;
  })();
  pending = request;
  try {
    return await request;
  } finally {
    if (pending === request) pending = null;
  }
};
